import {
  ATTACHMENT_EXTENSIONS,
  MAX_ATTACHMENT_BYTES,
  MAX_ATTACHMENTS_PER_ENTRY,
} from "@wodiary/shared";

/**
 * 上传前的本地校验（spec §5.3 `ENTRY-1`）。
 *
 * ⚠️ 这**不是**安全边界 —— 服务端会再校验一遍（`POST /api/entries/:id/attachments`）。
 * 这里的意义是让用户在选文件的当下就知道哪张不行，而不是等一次失败的上传。
 * 校验规则必须与服务端一致，否则会出现「本地放行、服务端 400」的困惑。
 */

export type RejectionReason = "too_large" | "bad_type" | "too_many";

export interface RejectedFile {
  readonly file: File;
  readonly reason: RejectionReason;
}

const ALLOWED_MIME = /^image\/(jpeg|jpg|png|webp|heic|heif)$/;

/** 单个文件本身合不合法（与数量无关）。合法返回 `null`。 */
export function validateImageFile(file: File): RejectionReason | null {
  if (file.size === 0) return "bad_type";
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  const byExtension = (ATTACHMENT_EXTENSIONS as readonly string[]).includes(ext);
  if (!byExtension && !ALLOWED_MIME.test(file.type)) return "bad_type";
  if (file.size > MAX_ATTACHMENT_BYTES) return "too_large";
  return null;
}

/**
 * 从一批选中的文件里挑出能加的：先按单文件校验，再按剩余名额截断。
 *
 * 超出名额的整批算 `too_many` —— 不静默丢弃，否则用户以为都加上了。
 */
export function selectAddable(
  files: readonly File[],
  existingCount: number,
): { accepted: File[]; rejected: RejectedFile[] } {
  const accepted: File[] = [];
  const rejected: RejectedFile[] = [];
  let remaining = Math.max(MAX_ATTACHMENTS_PER_ENTRY - existingCount, 0);

  for (const file of files) {
    const reason = validateImageFile(file);
    if (reason !== null) {
      rejected.push({ file, reason });
      continue;
    }
    if (remaining <= 0) {
      rejected.push({ file, reason: "too_many" });
      continue;
    }
    accepted.push(file);
    remaining -= 1;
  }
  return { accepted, rejected };
}

export function rejectionMessage(reason: RejectionReason): string {
  switch (reason) {
    case "too_large":
      return "超过 2MB";
    case "bad_type":
      return "不是支持的图片格式";
    case "too_many":
      return `最多 ${MAX_ATTACHMENTS_PER_ENTRY} 张`;
  }
}

/**
 * 读一张图片的宽高（用于占位比例）。
 *
 * 先试 `createImageBitmap`（快）；它比 `<img>` **扃剔** —— 实测一张 gray+alpha
 * 的 1×1 PNG 会被它以 `InvalidStateError` 拒掉，而 `<img>` 能正常渲染。所以
 * 失败时退回 `<img>` 量一次，与界面实际渲染的路径一致。
 *
 * 两条都失败（例如浏览器不认 HEIC）就退回 0×0，**不因此拒绝上传** —— 后端也
 * 只把宽高当呈现元数据。
 */
export async function readImageSize(
  file: File,
): Promise<{ width: number; height: number }> {
  try {
    const bitmap = await createImageBitmap(file);
    const size = { width: bitmap.width, height: bitmap.height };
    bitmap.close();
    return size;
  } catch {
    return sizeViaImageElement(file);
  }
}

function sizeViaImageElement(
  file: File,
): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({ width: 0, height: 0 });
    };
    img.src = url;
  });
}

export { MAX_ATTACHMENTS_PER_ENTRY, MAX_ATTACHMENT_BYTES };
